import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { forgotPassword } from '../lib/api';

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      // Endpoint always returns 200 to prevent enumeration; this is a network/server failure.
      toast.error('Could not request a reset right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F5F7] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-overlay" />
      <div className="w-full max-w-md relative z-10 animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="font-heading text-4xl text-[#3F617E] mb-2">Maris Desk</h1>
          <p className="text-stone-500 text-sm">Password reset</p>
        </div>

        <Card className="border-stone-200 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-center">Forgot password?</CardTitle>
            <CardDescription className="text-center">
              Enter your email and we’ll issue a one-time reset link valid for 1 hour.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitted ? (
              <div className="text-center space-y-3" data-testid="forgot-confirmation">
                <p className="text-sm text-stone-700">
                  Check your email — if <span className="font-medium">{email}</span> matches an account, we&rsquo;ve sent a reset link.
                </p>
                <p className="text-xs text-stone-500 leading-relaxed">
                  The link is valid for one hour and can only be used once. If you don&rsquo;t see it within a few minutes, please check your spam folder.
                </p>
                <Link
                  to="/login"
                  className="inline-flex items-center text-sm text-[#3F617E] hover:text-[#345470] underline-offset-4 hover:underline"
                  data-testid="back-to-login"
                >
                  <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to sign in
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full"
                  disabled={loading}
                  data-testid="forgot-submit-button"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Email me a reset link'}
                </Button>
                <p className="text-xs text-center text-stone-500">
                  <Link to="/login" className="text-[#3F617E] hover:underline" data-testid="back-to-login-link">
                    Back to sign in
                  </Link>
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
